import React, { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import { SURVEY_ENABLED, SURVEY_BATCHES, SURVEY_QUESTIONS } from './SurveyConfig';
// import { feedbackService } from '../../../services/apiService';
import { SurveyResponse, UserData, SurveyData, SurveyQuestion } from './types/survey';
import SurveyDialog from './SurveyDialog';

interface SurveyModalProps {
  appLoaded?: boolean;
  onSurveyStateChange?: (isOpen: boolean) => void;
  user?: any;
  userReady?: boolean;
}

const SurveyModal: React.FC<SurveyModalProps> = ({ appLoaded = false, onSurveyStateChange, user, userReady = false }) => {
  const [open, setOpen] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [responses, setResponses] = useState<SurveyResponse>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showIntro, setShowIntro] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showThankYou, setShowThankYou] = useState<boolean>(false);

  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const SURVEY_STORAGE_KEY = 'learner_survey_completed';
  
  const questionsPerStep = 5;
  const steps: SurveyQuestion[][] = [];
  for (let i = 0; i < SURVEY_QUESTIONS.length; i += questionsPerStep) {
    steps.push(SURVEY_QUESTIONS.slice(i, i + questionsPerStep));
  }

  useEffect(() => {
    if (user) {
      setOpen(false);
      setShowIntro(true);
      setShowThankYou(false);
      setCurrentStep(0);
      setResponses({});
      onSurveyStateChange?.(false);
    }
  }, [user?.id]);

  const checkEligibility = (): boolean => {
    const userData = localStorage.getItem('user');
    if (!userData) return false;

    try {
      const user: UserData = JSON.parse(userData);
      const userBatch = user.batchName || user['batch-name'];

      if (SURVEY_BATCHES.length === 0) return true;

      return SURVEY_BATCHES.includes(userBatch);
    } catch (error) {
      console.error('Error parsing user data:', error);
      return false;
    }
  };

  const checkSurveyStatus = async (): Promise<any> => {
    try {
      const studentId = localStorage.getItem('studentId');
      // const response = await feedbackService.get(`feedback/student/${studentId}/type/LEARNER_SURVEY`);
      // const surveyStatus = response.data;
      // return surveyStatus;
    } catch (error) {
      console.error('Failed to check survey status:', error);
      return false;
    }
  };

  const getUserSpecificStorageKey = (): string => {
    try {
      const studentId = localStorage.getItem('studentId');
      return `${SURVEY_STORAGE_KEY}_${studentId}`;
    } catch (error) {
      return SURVEY_STORAGE_KEY;
    }
  };

  useEffect(() => {
    const initializeSurvey = async (): Promise<void> => {
      if (!SURVEY_ENABLED || !appLoaded || !user || !userReady) return;

      const userSpecificKey = getUserSpecificStorageKey();

      const localStorageCompleted = localStorage.getItem(userSpecificKey) === 'true';

      if (localStorageCompleted) return;

      if (!checkEligibility()) return;

      setIsLoading(true);
      const surveyCompleted = await checkSurveyStatus();
      setIsLoading(false);

      if (surveyCompleted.totalCount === 0) {
        setOpen(true);
        onSurveyStateChange?.(true);
      } else {
        localStorage.setItem(userSpecificKey, 'true');
      }
    };

    initializeSurvey();
  }, [appLoaded, user?.id, userReady]);

  useEffect(() => {
    onSurveyStateChange?.(open);
  }, [open]);

  const handleResponseChange = (questionId: string, value: any): void => {
    setResponses(prev => ({ ...prev, [questionId]: value }));
  };

  const validateCurrentStep = (): boolean => {
    const currentQuestions = steps[currentStep];
    return currentQuestions.every(question => {
      if (!question.required) return true;

      const response = responses[question.id];
      if (!response) return false;

      if (question.type === 'conditional' && question.conditionalFields?.[response]) {
        return question.conditionalFields[response].every(field => {
          if (!field.required) return true;
          return responses[`${question.id}_${field.id}`];
        });
      }

      return true;
    });
  };

  const handleNext = (): void => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
      const dialogContent = document.querySelector('.MuiDialog-paper .MuiDialogContent-root');
      if (dialogContent) {
        dialogContent.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }
    }
  };

  const handleBack = (): void => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStartSurvey = (): void => {
    setShowIntro(false);
  };

  const handleSubmit = async (): Promise<void> => {
    setIsSubmitting(true);
    try {
      const studentId = localStorage.getItem('studentId');
      const surveyData: SurveyData = {
        studentId: studentId || '',
        feedbackType: 'LEARNER_SURVEY',
        touchpoint: 'HOME_PAGE',
        feedbackData: responses,
        isAnonymous: false
      };

      // await feedbackService.post('feedback', surveyData);
      localStorage.setItem(SURVEY_STORAGE_KEY, 'true');
      setShowThankYou(true);

      setTimeout(() => {
        setOpen(false);
        onSurveyStateChange?.(false);
      }, 2000);
    } catch (error) {
      console.error('Failed to submit survey:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const progress = ((currentStep + 1) / steps.length) * 100;

  if (!SURVEY_ENABLED || isLoading) return null;

  return (
    <SurveyDialog
      open={open}
      showIntro={showIntro}
      showThankYou={showThankYou}
      currentStep={currentStep}
      steps={steps}
      responses={responses}
      progress={progress}
      isSubmitting={isSubmitting}
      theme={theme}
      isDarkMode={isDarkMode}
      handleStartSurvey={handleStartSurvey}
      handleBack={handleBack}
      handleNext={handleNext}
      handleSubmit={handleSubmit}
      validateCurrentStep={validateCurrentStep}
      onResponseChange={handleResponseChange}
    />
  );
};

export default SurveyModal;
